import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Brain, X, Sparkles, ClipboardCheck, Lightbulb, CalendarClock, Settings2,
  Clock, Plus, Loader2, Linkedin, Facebook, ArrowRight, Send, MessageSquare, ListChecks,
  Target, Lock, Rocket, Infinity as InfinityIcon, Zap, History, Mail, Copy,
  Users, Download, Star, Trash2, UserPlus,
} from "lucide-react";

const ACTION_LABELS = { audit: "Audit Recent Strategy", concepts: "3 Post Concepts", windows: "Best Posting Windows", ask: "Your question" };

// Progressive "typing" reveal so answers stream in rather than popping in all at once.
function useTypewriter(text, speed = 12) {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    if (!text) return;
    const step = Math.max(1, Math.ceil(text.length / 120));
    const t = setInterval(() => setN((x) => { if (x >= text.length) { clearInterval(t); return x; } return x + step; }), speed);
    return () => clearInterval(t);
  }, [text, speed]);
  return { shown: text ? text.slice(0, n) : "", done: !text || n >= text.length };
}

const StrategyGroup = ({ group, onAdd, onDuplicate, onAsk, readOnly, animate }) => {
  const { shown, done: typed } = useTypewriter(animate ? group.summary : "", 14);
  const summary = animate ? shown : group.summary;
  const revealed = !animate || typed;
  const groupRef = useRef(null);
  useEffect(() => {
    if (animate && typed) groupRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [animate, typed]);
  const [queuing, setQueuing] = useState(false);
  const [done, setDone] = useState(() => new Set());
  const add = async (c, silent) => {
    if (done.has(c.id)) return true;
    const ok = await onAdd(c, silent);
    if (ok) setDone((d) => new Set(d).add(c.id));
    return ok;
  };
  const pending = group.cards.filter((c) => !done.has(c.id));
  const queueAll = async () => {
    setQueuing(true);
    const results = await Promise.all(pending.map((c) => add(c, true)));
    setQueuing(false);
    const ok = results.filter(Boolean).length;
    if (ok) toast.success(`${ok} post${ok > 1 ? "s" : ""} queued at their best times`);
  };
  return (
    <div ref={groupRef} className="space-y-3 scroll-mt-4" data-testid={`strategy-group-${group.id}`}>
      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
        <ArrowRight className="h-3 w-3" /> {group.label}
      </p>
      {group.question && (
        <div className="ml-8 rounded-xl rounded-tr-sm bg-primary/15 border border-primary/30 px-3 py-2 text-sm" data-testid="strategy-question-bubble">{group.question}</div>
      )}
      {group.summary && (
        <div className="rounded-xl rounded-tl-sm border border-border bg-secondary/40 px-3.5 py-3 text-sm leading-relaxed text-foreground/90" data-testid="strategy-summary">
          <span className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-primary mb-1.5"><Brain className="h-3 w-3" /> Strategist</span>
          {summary}{!revealed && <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-primary/70 animate-pulse align-middle" />}
        </div>
      )}
      {revealed && group.cards.map((card, i) => (
        <div key={card.id} style={{ animationDelay: `${i * 120}ms` }} className="animate-fadeUp">
          <StrategyCard card={card} onAdd={add} onDuplicate={onDuplicate} readOnly={readOnly} added={done.has(card.id)} locked={queuing} />
        </div>
      ))}
      {revealed && group.cards.length > 1 && !readOnly && (
        <Button size="sm" variant="outline" onClick={queueAll} disabled={queuing || pending.length === 0} data-testid={`strategy-queue-all-${group.id}`}
          className="w-full border-primary/40 text-primary hover:bg-primary/10">
          {queuing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ListChecks className="h-4 w-4 mr-2" />}
          {queuing ? `Queuing ${pending.length} posts…` : pending.length === 0 ? "All queued to your calendar" : `Queue ${pending.length === group.cards.length ? "all " : ""}${pending.length} at their best times`}
        </Button>
      )}
      {revealed && group.follow_ups?.length > 0 && !readOnly && onAsk && (
        <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`strategy-followups-${group.id}`}>
          {group.follow_ups.map((q, i) => (
            <button key={i} onClick={() => onAsk(q)} data-testid={`strategy-followup-${group.id}-${i}`}
              className="text-left text-[11px] rounded-full border border-border bg-background px-2.5 py-1 text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
function relTime(iso) {
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const PLATFORM_META = {
  linkedin: { label: "LinkedIn", icon: Linkedin, cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  facebook: { label: "Facebook", icon: Facebook, cls: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30" },
};

const ACTIONS = [
  { key: "audit", label: "Audit Recent Strategy", icon: ClipboardCheck },
  { key: "concepts", label: "Generate 3 Post Concepts", icon: Lightbulb },
  { key: "windows", label: "Best Posting Windows", icon: CalendarClock },
];

const INDUSTRIES = [
  "Local Service", "E-Commerce", "Agency", "Real Estate", "Restaurant / Cafe",
  "SaaS / Tech", "Health & Wellness", "Coaching / Consulting", "Creator / Influencer", "Other",
];
const GOALS = ["Lead Generation", "Brand Awareness", "Sales", "Local Engagement", "Website Traffic", "Community Growth"];
const TONES = ["Professional", "Casual", "Energetic", "Friendly", "Bold", "Educational"];

// Convert a "Tue 9:15 AM" style label into the next matching future ISO datetime.
function suggestedToISO(label) {
  const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const m = /^(\w{3})\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(label || "");
  const d = new Date();
  if (!m) { d.setDate(d.getDate() + 1); return d.toISOString(); }
  const target = days.indexOf(m[1].toLowerCase());
  let hour = parseInt(m[2], 10) % 12;
  if (/pm/i.test(m[4])) hour += 12;
  let diff = (target - d.getDay() + 7) % 7;
  if (diff === 0) diff = 7;
  d.setDate(d.getDate() + diff);
  d.setHours(hour, parseInt(m[3], 10), 0, 0);
  return d.toISOString();
}

const StrategyCard = ({ card, onAdd, onDuplicate, readOnly, added = false, locked = false }) => {
  const meta = PLATFORM_META[card.platform] || PLATFORM_META.facebook;
  const Icon = meta.icon;
  const [own, setOwn] = useState("idle"); // idle | saving | added
  const state = added ? "added" : own;
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 animate-fadeUp" data-testid={`strategy-card-${card.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Badge className={`border ${meta.cls} gap-1.5`} data-testid={`strategy-card-platform-${card.id}`}>
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </Badge>
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary" data-testid={`strategy-card-objective-${card.id}`}>
          <Target className="h-3.5 w-3.5" /> {card.objective}
        </span>
      </div>
      <div className="text-sm text-foreground/90 leading-relaxed space-y-2" data-testid={`strategy-card-copy-${card.id}`}>
        {String(card.copy || "").split(/\n\s*\n/).map((para, i) => <p key={i} className="whitespace-pre-line">{para}</p>)}
      </div>
      {card.hashtags?.length > 0 && (
        <p className="text-xs text-accent font-mono" data-testid={`strategy-card-hashtags-${card.id}`}>{card.hashtags.join("  ")}</p>
      )}
      <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
        <Clock className="h-3.5 w-3.5" /> <span data-testid={`strategy-card-time-${card.id}`}>{card.time}</span>
      </div>
      {card.reason && (
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground border-l-2 border-primary/40 pl-2.5">
          <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
          <span data-testid={`strategy-card-reason-${card.id}`}><span className="font-semibold text-foreground/80">Why it works: </span>{card.reason}</span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={state !== "idle" || readOnly || locked}
          onClick={async () => { setOwn("saving"); const ok = await onAdd(card); setOwn(ok ? "added" : "idle"); }}
          data-testid={`strategy-add-to-queue-${card.id}`}
          className="flex-1 bg-primary hover:bg-primary/90 text-white"
        >
          {state === "added" ? (<><ClipboardCheck className="h-4 w-4 mr-2" /> Scheduled</>)
            : state === "saving" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scheduling…</>)
            : (<><CalendarClock className="h-4 w-4 mr-2" /> Schedule for {card.time}</>)}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDuplicate?.(card)} title="Duplicate & tweak"
          data-testid={`strategy-duplicate-${card.id}`} className="border-border shrink-0 px-2.5">
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const ProfileModal = ({ open, onOpenChange, ws, onSaved }) => {
  const { refresh } = useWorkspace();
  const [form, setForm] = useState({ business_type: "", target_audience: "", main_goal: "", tone: "", audit_cadence: "weekly", report_logo_url: "", report_accent: "#6366F1", pain_points: "", unpopular_opinions: "", products_to_promote: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ws) setForm({
      business_type: ws.business_type || "", target_audience: ws.target_audience || "",
      main_goal: ws.main_goal || "", tone: ws.tone || "", audit_cadence: ws.audit_cadence || "weekly",
      report_logo_url: ws.report_logo_url || "", report_accent: ws.report_accent || "#6366F1",
      pain_points: ws.pain_points || "", unpopular_opinions: ws.unpopular_opinions || "", products_to_promote: ws.products_to_promote || "",
    });
  }, [ws, open]);

  const withValue = (list, val) => (val && !list.includes(val) ? [val, ...list] : list);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${ws.id}`, form);
      await refresh();
      toast.success("Business profile saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md z-[90]" data-testid="business-profile-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="h-4.5 w-4.5 text-primary" /> Business Profile</DialogTitle>
          <DialogDescription>Tailors every strategy card to {ws?.brand_name || ws?.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Industry / Business Type</Label>
            <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="profile-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
              <SelectContent>{withValue(INDUSTRIES, form.business_type).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Target Audience / Ideal Customer</Label>
            <Input className="mt-1.5" data-testid="profile-audience" placeholder="e.g. Remote workers aged 25-40 nearby"
              value={form.target_audience} onChange={(e) => setForm((f) => ({ ...f, target_audience: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Main Goal</Label>
            <Select value={form.main_goal} onValueChange={(v) => setForm((f) => ({ ...f, main_goal: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="profile-goal"><SelectValue placeholder="Select goal" /></SelectTrigger>
              <SelectContent>{withValue(GOALS, form.main_goal).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tone of Voice</Label>
            <Select value={form.tone} onValueChange={(v) => setForm((f) => ({ ...f, tone: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="profile-tone"><SelectValue placeholder="Select tone" /></SelectTrigger>
              <SelectContent>{withValue(TONES, form.tone).map((tn) => <SelectItem key={tn} value={tn}>{tn}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="pt-1 border-t border-border">
            <Label className="text-xs flex items-center gap-1.5 mt-3"><Sparkles className="h-3.5 w-3.5 text-primary" /> Expert grounding (SME engine)</Label>
            <p className="text-[11px] text-muted-foreground mt-1">Grounds every generated post in real domain authority.</p>
            <textarea data-testid="profile-pain-points" rows={2} placeholder="Audience pain points (e.g. wasted hours on manual scheduling, inconsistent posting)"
              className="mt-2 w-full rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm resize-none"
              value={form.pain_points} onChange={(e) => setForm((f) => ({ ...f, pain_points: e.target.value }))} />
            <textarea data-testid="profile-unpopular" rows={2} placeholder="Unpopular opinions / strong beliefs (used as hook fuel)"
              className="mt-2 w-full rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm resize-none"
              value={form.unpopular_opinions} onChange={(e) => setForm((f) => ({ ...f, unpopular_opinions: e.target.value }))} />
            <textarea data-testid="profile-products" rows={2} placeholder="Products/services to soft-sell (woven in naturally)"
              className="mt-2 w-full rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm resize-none"
              value={form.products_to_promote} onChange={(e) => setForm((f) => ({ ...f, products_to_promote: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-primary" /> Auto-audit email</Label>
            <Select value={form.audit_cadence} onValueChange={(v) => setForm((f) => ({ ...f, audit_cadence: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="profile-cadence"><SelectValue placeholder="Cadence" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly (every Monday)</SelectItem>
                <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="off">Off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="pt-1 border-t border-border">
            <Label className="text-xs flex items-center gap-1.5 mt-3"><Download className="h-3.5 w-3.5 text-primary" /> Client report branding</Label>
            <Input className="mt-1.5" data-testid="profile-logo" placeholder="Logo image URL (https://…)"
              value={form.report_logo_url} onChange={(e) => setForm((f) => ({ ...f, report_logo_url: e.target.value }))} />
            <div className="flex items-center gap-2 mt-2">
              <input type="color" data-testid="profile-accent" value={form.report_accent}
                onChange={(e) => setForm((f) => ({ ...f, report_accent: e.target.value }))}
                className="h-9 w-12 rounded-md border border-border bg-transparent cursor-pointer" />
              <span className="text-xs text-muted-foreground">Accent color on your PDF report</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="w-full bg-primary hover:bg-primary/90 text-white" data-testid="profile-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UpgradeModal = ({ open, onOpenChange, kind, resetDate }) => {
  const navigate = useNavigate();
  const go = () => { onOpenChange(false); navigate("/billing"); };
  const isLimit = kind === "limit_reached";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-border bg-card p-0 overflow-hidden z-[90]" data-testid="strategy-upgrade-modal">
        <DialogTitle className="sr-only">{isLimit ? "Upgrade to Agency" : "Upgrade to Growth"}</DialogTitle>
        <div className="bg-gradient-to-br from-primary/20 via-transparent to-accent/10 p-6 sm:p-7">
          <div className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-primary/15 text-primary border border-primary/30 mb-4">
            {isLimit ? <Rocket className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {isLimit ? "Unlock Unlimited AI Strategy" : "AI Marketing Strategist"}
          </div>
          {isLimit ? (
            <>
              <h2 className="text-xl font-display font-bold tracking-tight">🚀 Unlock Unlimited AI Strategy & Scale Your Growth</h2>
              <p className="text-sm text-muted-foreground mt-2">
                You've used all 50 AI strategy runs for this billing cycle on the Growth Plan. Your previously
                generated strategy cards and scheduled posts remain fully active in your queue.
              </p>
              <p className="text-sm text-muted-foreground mt-2">Upgrade to <span className="text-foreground font-medium">Agency ($199/mo)</span> to keep generating without interruption:</p>
              <ul className="mt-4 space-y-2 text-sm">
                {[
                  ["♾️", "Unlimited AI Strategy Runs — no caps or throttling"],
                  ["🏢", "Unlimited workspaces & client brands"],
                  ["🎯", "Advanced brand voice customization"],
                  ["⚡", "Priority queueing & processing"],
                ].map(([e, t]) => (
                  <li key={t} className="flex items-start gap-2"><span className="shrink-0">{e}</span><span>{t}</span></li>
                ))}
              </ul>
              <Button onClick={go} size="lg" className="w-full mt-6 bg-primary hover:bg-primary/90 text-white" data-testid="upgrade-to-agency-btn">
                Upgrade to Agency ($199/mo) <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <button onClick={() => onOpenChange(false)} className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors">
                Keep Current Plan{resetDate ? ` (resets ${new Date(resetDate).toLocaleDateString()})` : ""}
              </button>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-display font-bold tracking-tight">Upgrade to Growth to unlock your AI Marketing Strategist</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Your Creator plan covers unlimited campaigns and scheduling. The AI Strategy Consultant audits your strategy,
                generates ready-to-post concepts and finds your best posting windows.
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                {[
                  "50 AI strategy runs every month",
                  "Shopify catalog sync & inventory-aware auto-swap",
                  "10 social accounts & full scheduling",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2"><Zap className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /><span>{t}</span></li>
                ))}
              </ul>
              <Button onClick={go} size="lg" className="w-full mt-6 bg-primary hover:bg-primary/90 text-white" data-testid="upgrade-to-growth-btn">
                Upgrade to E-commerce Growth ($79/mo) <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditCardModal = ({ card, onOpenChange, onSchedule }) => {
  const [copy, setCopy] = useState("");
  const [time, setTime] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [platform, setPlatform] = useState("facebook");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (card) {
      setCopy(card.copy || "");
      setTime(card.time || "");
      setHashtags((card.hashtags || []).join(" "));
      setPlatform(card.platform || "facebook");
    }
  }, [card]);

  const schedule = async () => {
    setSaving(true);
    const tags = hashtags.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean).map((t) => (t.startsWith("#") ? t : `#${t}`));
    const ok = await onSchedule({ ...card, copy, time, hashtags: tags, platform });
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="sm:max-w-md z-[90]" data-testid="strategy-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="h-4.5 w-4.5 text-primary" /> Duplicate & tweak</DialogTitle>
          <DialogDescription>Adjust this winning card, then reschedule it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="mt-1.5" data-testid="edit-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PLATFORM_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Post copy</Label>
            <textarea data-testid="edit-copy" value={copy} onChange={(e) => setCopy(e.target.value)} rows={5}
              className="mt-1.5 w-full rounded-md border border-border bg-secondary/30 p-2.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <Label className="text-xs">Hashtags</Label>
            <Input className="mt-1.5" data-testid="edit-hashtags" value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#one #two" />
          </div>
          <div>
            <Label className="text-xs">Suggested time</Label>
            <Input className="mt-1.5" data-testid="edit-time" value={time} onChange={(e) => setTime(e.target.value)} placeholder="Tue 9:00 AM" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={schedule} disabled={saving || !copy.trim()} className="w-full bg-primary hover:bg-primary/90 text-white" data-testid="edit-schedule">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />} Schedule this version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TeamModal = ({ open, onOpenChange, ws }) => {
  const { refresh } = useWorkspace();
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (ws) setMembers(ws.members || []); }, [ws, open]);

  const add = async (em, rl) => {
    const targetEmail = em ?? email;
    if (!targetEmail.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${ws.id}/members`, { email: targetEmail.trim(), role: rl ?? role });
      setMembers(r.data.members);
      if (!em) setEmail("");
      await refresh();
      if (!em) toast.success("Teammate added", { description: "They'll see this workspace when they log in." });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not add teammate"));
    } finally { setBusy(false); }
  };

  const remove = async (em) => {
    setBusy(true);
    try {
      const r = await api.delete(`/workspaces/${ws.id}/members?email=${encodeURIComponent(em)}`);
      setMembers(r.data.members);
      await refresh();
    } catch (e) { toast.error("Could not remove teammate"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md z-[90]" data-testid="team-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-4.5 w-4.5 text-primary" /> Team seats</DialogTitle>
          <DialogDescription>Invite teammates to collaborate on {ws?.brand_name || ws?.name}. They log in with their own account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex gap-2">
            <Input data-testid="team-email" type="email" placeholder="teammate@email.com" value={email}
              onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-[104px] shrink-0" data-testid="team-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => add()} disabled={busy} data-testid="team-add" className="bg-primary hover:bg-primary/90 text-white shrink-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            </Button>
          </div>
          {members.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2 text-center">No teammates yet. Add up to 9.</p>
          ) : (
            <ul className="space-y-1.5">
              {members.map((m) => (
                <li key={m.email} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`team-member-${m.email}`}>
                  <span className="truncate flex-1 min-w-0">{m.email}</span>
                  <Select value={m.role || "editor"} onValueChange={(v) => add(m.email, v)}>
                    <SelectTrigger className="w-[92px] h-7 text-xs shrink-0" data-testid={`team-member-role-${m.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={() => remove(m.email)} className="text-muted-foreground hover:text-red-400 transition-colors shrink-0" data-testid={`team-remove-${m.email}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function StrategyConsultantPanel({ open, onOpenChange, onQueued }) {
  const { active } = useWorkspace();
  const { user } = useAuth();
  const [loadingAction, setLoadingAction] = useState(null);
  const [feed, setFeed] = useState([]);
  const [usage, setUsage] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [upgrade, setUpgrade] = useState(null); // {kind, resetDate}
  const [view, setView] = useState("live"); // "live" | "history"
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [winners, setWinners] = useState([]);
  const [teamOpen, setTeamOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const hasConsultant = !!user?.has_consultant || (!!active && active.is_owner === false);
  const isViewer = active?.my_role === "viewer";

  const loadUsage = useCallback(async () => {
    if (!active) return;
    try {
      const [u, w] = await Promise.all([
        api.get(`/strategy/usage?workspace_id=${active.id}`),
        api.get(`/strategy/winners?workspace_id=${active.id}`),
      ]);
      setUsage(u.data);
      setWinners(w.data || []);
    } catch (e) { /* ignore */ }
  }, [active]);

  // Reset feed when the active workspace changes.
  useEffect(() => { setFeed([]); setView("live"); }, [active]);

  const loadHistory = useCallback(async () => {
    if (!active) return;
    setLoadingHistory(true);
    try {
      const [h, w] = await Promise.all([
        api.get(`/strategy/history?workspace_id=${active.id}&limit=20`),
        api.get(`/strategy/winners?workspace_id=${active.id}`),
      ]);
      setHistory(h.data || []);
      setWinners(w.data || []);
    } catch (e) { /* ignore */ } finally { setLoadingHistory(false); }
  }, [active]);

  const downloadReport = async () => {
    if (!active) return;
    setDownloading(true);
    try {
      const r = await api.get(`/strategy/report?workspace_id=${active.id}`);
      const blob = new Blob([r.data.html], { type: "text/html" });
      window.open(URL.createObjectURL(blob), "_blank", "noopener");
    } catch (e) {
      toast.error("Could not build report");
    } finally { setDownloading(false); }
  };

  const toggleHistory = () => {
    const next = view === "history" ? "live" : "history";
    setView(next);
    if (next === "history") loadHistory();
  };

  const emailAudit = async () => {
    if (!active) return;
    setEmailing(true);
    try {
      const r = await api.post("/strategy/audit-now", { workspace_id: active.id });
      if (r.data?.sent) toast.success("Audit emailed!", { description: "Check your inbox for this week's strategy audit." });
      else toast.info("Audit generated & saved to History", { description: "Email delivery is limited until your sending domain is verified." });
      if (view === "history") loadHistory();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not send audit"));
    } finally { setEmailing(false); }
  };

  // Gate on open: Starter (no consultant) → upgrade modal instead of the panel.
  useEffect(() => {
    if (!open) return;
    if (!hasConsultant) { onOpenChange(false); setUpgrade({ kind: "upgrade_growth" }); return; }
    loadUsage();
  }, [open, hasConsultant, loadUsage, onOpenChange]);

  const [question, setQuestion] = useState("");
  const feedEndRef = useRef(null);
  useEffect(() => { feedEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [feed.length, loadingAction]);
  const [latestId, setLatestId] = useState(null);
  const ask = (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setQuestion("");
    runAction({ key: "ask", label: "Your question" }, text);
  };

  const runAction = async (action, q) => {
    if (!active) return;
    if (usage?.limit != null && usage?.used >= usage.limit) {
      setUpgrade({ kind: "limit_reached", resetDate: usage?.reset_date });
      return;
    }
    setLoadingAction(action.key);
    try {
      const r = await api.post("/strategy/generate", { workspace_id: active.id, action: action.key, question: q || undefined });
      const gid = `${action.key}-${Date.now()}`;
      setLatestId(gid);
      setFeed((prev) => [...prev, { id: gid, label: action.label, question: q || "", summary: r.data.summary || "", follow_ups: r.data.follow_ups || [], cards: r.data.cards }]);
      if (r.data.usage) setUsage((u) => ({ ...(u || {}), ...r.data.usage }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "upgrade_growth") setUpgrade({ kind: "upgrade_growth" });
      else if (detail?.code === "limit_reached") setUpgrade({ kind: "limit_reached", resetDate: detail?.reset_date });
      else toast.error(typeof detail === "string" ? detail : "Could not generate strategy");
    } finally { setLoadingAction(null); }
  };

  // Persist the card as a real QUEUED post so it shows up in Calendar & Dashboard.
  const handleAdd = async (card, silent) => {
    if (!active) return false;
    const meta = PLATFORM_META[card.platform] || PLATFORM_META.facebook;
    const text = card.hashtags?.length ? `${card.copy}\n\n${card.hashtags.join(" ")}` : card.copy;
    try {
      await api.post("/posts", {
        workspace_id: active.id,
        platform: card.platform,
        post_text: text,
        category: "AI Strategy",
        scheduled_at: suggestedToISO(card.time),
        recycle_enabled: true,
        origin: "strategy",
        strategy_objective: card.objective || null,
      });
      if (!silent) toast.success("Post successfully queued!", { description: `${meta.label} · ${card.time}` });
      onQueued?.();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue post");
      return false;
    }
  };

  const profileComplete = usage?.profile_complete ?? (active ? !!(active.business_type && active.target_audience && active.main_goal && active.tone) : false);
  const limitReached = usage?.limit != null && usage?.used >= usage.limit;

  return (
    <>
      {/* Floating launcher (also openable from the sidebar nav) */}
      {!open && (
        <button
          onClick={() => onOpenChange(true)}
          data-testid="strategy-consultant-open"
          className="fixed right-4 bottom-6 z-[55] flex items-center gap-2 rounded-full bg-primary text-white pl-3 pr-4 py-2.5 shadow-lg shadow-primary/25 hover:bg-primary/90 transition-colors"
        >
          <Brain className="h-5 w-5" />
          <span className="text-sm font-medium hidden sm:inline">AI Strategy Consultant</span>
        </button>
      )}

      <div
        onClick={() => onOpenChange(false)}
        className={`fixed inset-0 z-[65] bg-black/50 backdrop-blur-sm transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />

      <div className="fixed inset-0 z-[66] overflow-hidden pointer-events-none">
      <aside
        data-testid="strategy-consultant-panel"
        className={`absolute top-0 right-0 h-full w-full sm:w-[400px] bg-background border-l border-border flex flex-col transition-transform duration-300 ease-out pointer-events-auto ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
              <Brain className="h-4.5 w-4.5" />
            </div>
            <div>
              <h3 className="font-display font-bold text-sm leading-tight">AI Strategy Consultant</h3>
              <p className="text-[11px] text-muted-foreground">{active?.brand_name || active?.name || "Your grid"}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {active?.is_owner !== false && (
              <button onClick={() => setTeamOpen(true)} data-testid="strategy-team-open"
                className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" title="Team seats">
                <Users className="h-4.5 w-4.5" />
              </button>
            )}
            <button onClick={downloadReport} data-testid="strategy-report" disabled={downloading}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-50" title="Download client report">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4.5 w-4.5" />}
            </button>
            <button onClick={toggleHistory} data-testid="strategy-history-toggle"
              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${view === "history" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`} title="Strategy history">
              <History className="h-4.5 w-4.5" />
            </button>
            <button onClick={() => setProfileOpen(true)} data-testid="strategy-profile-open"
              className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" title="Business profile">
              <Settings2 className="h-4.5 w-4.5" />
            </button>
            <button onClick={() => onOpenChange(false)} data-testid="strategy-consultant-close"
              className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              <X className="h-4.5 w-4.5" />
            </button>
          </div>
        </div>

        {/* Usage meter */}
        {usage && (
          <div className="px-5 pt-3 shrink-0" data-testid="strategy-usage-meter">
            {usage.limit == null ? (
              <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
                <InfinityIcon className="h-3.5 w-3.5" /> Unlimited strategy runs
              </div>
            ) : (
              <>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-muted-foreground">Strategy runs this month</span>
                  <span className="font-mono">{usage.used} / {usage.limit}</span>
                </div>
                <Progress value={(usage.used / usage.limit) * 100} className="h-1.5" />
              </>
            )}
          </div>
        )}

        {/* Quick actions */}
        <div className="px-5 py-4 border-b border-border shrink-0 space-y-2">
          {ACTIONS.map((a) => (
            <Button
              key={a.key}
              variant="outline"
              onClick={() => runAction(a)}
              disabled={loadingAction === a.key || !profileComplete || limitReached || isViewer}
              data-testid={`strategy-action-${a.key}`}
              className="w-full justify-start border-border hover:bg-secondary/60 hover:border-primary/40"
            >
              {loadingAction === a.key
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin text-primary" />
                : <a.icon className="h-4 w-4 mr-2 text-primary" />}
              {a.label}
            </Button>
          ))}
          {limitReached && (
            <button onClick={() => setUpgrade({ kind: "limit_reached", resetDate: usage?.reset_date })}
              data-testid="strategy-limit-cta" className="w-full text-xs text-primary hover:underline pt-1">
              You've hit your monthly limit — upgrade to Agency for unlimited →
            </button>
          )}
          {profileComplete && !isViewer && (
            <button onClick={emailAudit} disabled={emailing} data-testid="strategy-email-audit"
              className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors pt-1 disabled:opacity-60">
              {emailing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
              Email me this week's audit
            </button>
          )}
          {isViewer && (
            <p data-testid="viewer-note" className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1">
              <Lock className="h-3.5 w-3.5" /> View-only access — browse strategy, but generating &amp; scheduling are disabled.
            </p>
          )}
        </div>

        {/* History view */}
        {view === "history" ? (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6" data-testid="strategy-history">
            {loadingHistory ? (
              <div className="h-full flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : history.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-12">
                <History className="h-8 w-8 mb-3 opacity-50" />
                <p className="text-sm max-w-[220px]">No past runs yet. Generate a strategy to build your history.</p>
              </div>
            ) : (
              <>
                {winners.length > 0 && (
                  <div className="space-y-3" data-testid="strategy-winners">
                    <p className="text-xs font-mono uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                      <Star className="h-3 w-3 fill-amber-400" /> Proven winners
                    </p>
                    {winners.map((w, i) => {
                      const meta = PLATFORM_META[w.platform] || PLATFORM_META.facebook;
                      const Icon = meta.icon;
                      return (
                        <div key={i} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2" data-testid={`winner-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <Badge className={`border ${meta.cls} gap-1.5`}><Icon className="h-3.5 w-3.5" /> {meta.label}</Badge>
                            <span className="text-[11px] font-medium text-emerald-400">Scheduled {w.scheduled}× · Published {w.published}×</span>
                          </div>
                          <p className="text-sm text-foreground/90 line-clamp-3">{w.post_text}</p>
                          <Button size="sm" variant="outline" className="w-full border-border"
                            data-testid={`winner-reuse-${i}`}
                            onClick={() => setEditCard({ id: `winner-${i}`, platform: w.platform, objective: w.objective || "Reuse winner", copy: w.post_text, hashtags: [], time: "Tue 9:00 AM", reason: "" })}>
                            <Copy className="h-4 w-4 mr-2" /> Reuse & tweak
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {history.map((run) => (
                <div key={run.id} className="space-y-3" data-testid={`history-run-${run.id}`}>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                    <span className="flex items-center gap-1.5"><ArrowRight className="h-3 w-3" /> {ACTION_LABELS[run.action] || run.action}</span>
                    <span className="normal-case tracking-normal flex items-center gap-1">
                      {run.source === "weekly_email" && <Mail className="h-3 w-3 text-primary" />}{relTime(run.created_at)}
                    </span>
                  </p>
                    {run.question && <div className="ml-8 rounded-xl rounded-tr-sm bg-primary/15 border border-primary/30 px-3 py-2 text-sm">{run.question}</div>}
                  {run.summary && <div className="rounded-xl border border-border bg-secondary/40 px-3.5 py-3 text-sm leading-relaxed text-foreground/90">{run.summary}</div>}
                  {(run.cards || []).map((card, i) => (
                    <StrategyCard key={`${run.id}-${i}`} card={{ ...card, id: `${run.id}-${i}` }} onAdd={handleAdd} onDuplicate={setEditCard} readOnly={isViewer} />
                  ))}
                </div>
                ))}
              </>
            )}
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6" data-testid="strategy-feed">
          {profileComplete && !isViewer && winners.length > 0 && (() => {
            const w = winners.find((x) => x.published > 0) || winners[0];
            return (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2" data-testid="winner-suggestion">
                <p className="text-xs font-mono uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                  <Star className="h-3 w-3 fill-amber-400" /> Suggested this week — re-run a top performer
                </p>
                <p className="text-sm text-foreground/90 line-clamp-3">{w.post_text}</p>
                <p className="text-[11px] text-emerald-400">Scheduled {w.scheduled}× · Published {w.published}×</p>
                <Button size="sm" variant="outline" className="w-full border-amber-500/40" data-testid="winner-suggestion-reuse"
                  onClick={() => setEditCard({ id: "suggest", platform: w.platform, objective: w.objective || "Reuse winner", copy: w.post_text, hashtags: [], time: "Tue 9:00 AM", reason: "" })}>
                  <Copy className="h-4 w-4 mr-2" /> Reuse this winner
                </Button>
              </div>
            );
          })()}
          {!profileComplete ? (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="strategy-setup-banner">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                  <Settings2 className="h-4.5 w-4.5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Set up {active?.brand_name || active?.name || "this workspace"}'s Brand Profile to enable personalized AI strategy.</p>
                  <p className="text-xs text-muted-foreground mt-1">Add your industry, audience, goal and tone so every recommendation fits your brand.</p>
                  <Button size="sm" onClick={() => setProfileOpen(true)} data-testid="strategy-setup-banner-cta"
                    className="mt-3 bg-primary hover:bg-primary/90 text-white">
                    <Settings2 className="h-4 w-4 mr-2" /> Set up Brand Profile
                  </Button>
                </div>
              </div>
            </div>
          ) : feed.length === 0 && !loadingAction ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-12">
              <Sparkles className="h-8 w-8 mb-3 opacity-50" />
              <p className="text-sm max-w-[240px]">Pick a quick action above, or ask your strategist anything about growing {active?.brand_name || "your brand"} on Facebook &amp; LinkedIn.</p>
              {!isViewer && (
                <div className="flex flex-wrap justify-center gap-1.5 mt-4" data-testid="strategy-starter-prompts">
                  {["What should I post this week to drive sales?", "Which product should I push on LinkedIn?", "How do I turn followers into buyers?"].map((q, i) => (
                    <button key={i} onClick={() => ask(q)} data-testid={`strategy-starter-${i}`}
                      className="text-[11px] rounded-full border border-border bg-background px-2.5 py-1 hover:text-foreground hover:border-primary/50 transition-colors">{q}</button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {feed.map((group) => (
                <StrategyGroup key={group.id} group={group} onAdd={handleAdd} onDuplicate={setEditCard}
                  onAsk={profileComplete && !limitReached ? ask : null} readOnly={isViewer} animate={group.id === latestId} />
              ))}
              {loadingAction && (
                <div className="rounded-xl border border-border bg-secondary/40 px-3.5 py-3 text-sm text-muted-foreground flex items-center gap-2" data-testid="strategy-thinking">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" /> Studying your brand, site and recent posts…
                </div>
              )}
              <div ref={feedEndRef} />
            </>
          )}
        </div>
        )}
        {view !== "history" && profileComplete && !isViewer && (
          <div className="border-t border-border px-4 py-3 shrink-0" data-testid="strategy-ask-bar">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="strategy-ask-input"
                onKeyDown={(e) => e.key === "Enter" && !loadingAction && ask()}
                placeholder="Ask your strategist anything…" className="h-9 bg-secondary/40" disabled={!!loadingAction || limitReached} />
              <Button size="sm" onClick={() => ask()} disabled={!question.trim() || !!loadingAction || limitReached}
                data-testid="strategy-ask-send" className="h-9 bg-primary hover:bg-primary/90 text-white shrink-0 px-3">
                {loadingAction === "ask" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}
      </aside>
      </div>

      <ProfileModal open={profileOpen} onOpenChange={setProfileOpen} ws={active} onSaved={loadUsage} />
      <UpgradeModal open={!!upgrade} onOpenChange={(o) => !o && setUpgrade(null)} kind={upgrade?.kind} resetDate={upgrade?.resetDate} />
      <EditCardModal card={editCard} onOpenChange={(o) => !o && setEditCard(null)} onSchedule={handleAdd} />
      <TeamModal open={teamOpen} onOpenChange={setTeamOpen} ws={active} />
    </>
  );
}
